import {
    ArrowLeft,
    Bookmark,
    BookOpen,
    ChevronLeft,
    ChevronRight,
    ExternalLink,
    FileText,
    Folder,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { useSearchParams } from 'react-router-dom'
import ROUTES from '@/ROUTES'

import { IPublicResource } from '@/MODELS/publicResource.model'

import analytics from '@/services/analytics.service'
import { api } from '@/services/api.service'
import { useAuth } from '@/services/authentication.service'

import { DataView } from '@/components/data-view/DataView'
import { FilterInput } from '@/components/data-view/FilterInput'
import Loading from '@/components/Loading'
import { SectionHeader } from '@/components/SectionHeader'

export default function ResourceLibraryPage() {
    const { user, fetchUser } = useAuth()
    const navigate = useNavigate()

    const [searchParams] = useSearchParams()

    const [selectedCategory, setSelectedCategory] = useState<string>()
    const [currentPage, setCurrentPage] = useState(1)
    const [itemsPerPage, setItemsPerPage] = useState(9)

    useEffect(() => {
        const fetchedCategory = searchParams.get('category')
        setSelectedCategory(fetchedCategory)
    }, [searchParams])

    useEffect(() => {
        setCurrentPage(1)
    }, [selectedCategory])

    return (
        <DataView<IPublicResource> endpoint={ROUTES.app.publicResource}>
            {({ data, filter, isLoading }) => {
                let filteredData = data

                if (selectedCategory && selectedCategory !== 'All Resources') {
                    const normalizedSelected = selectedCategory.trim().toLowerCase()
                    filteredData = data?.filter(
                        (resource) => resource.category?.trim().toLowerCase() === normalizedSelected
                    )
                }

                const totalItems = filteredData?.length || 0
                const totalPages = Math.ceil(totalItems / itemsPerPage)
                const startIndex = (currentPage - 1) * itemsPerPage
                const endIndex = startIndex + itemsPerPage
                const paginatedData = filteredData?.slice(startIndex, endIndex)

                function handleToggleFavorite(resourceId: string): void {
                    if (!user) return

                    const resourceToToggle = data?.find((res) => res._id === resourceId)

                    if (!resourceToToggle) return

                    const isAlreadyFavorited = user.favoriteResources.some(
                        (favRes) => favRes?._id?.toString() === resourceId?.toString()
                    )

                    const updatedFavorites = isAlreadyFavorited
                        ? user.favoriteResources.filter((favRes) => favRes._id !== resourceId)
                        : [...user.favoriteResources, resourceToToggle]

                    api(ROUTES.app.user, { _id: user._id, favoriteResources: updatedFavorites }).then(() => {
                        fetchUser()
                    })
                }

                return (
                    <div className='flex-1 w-full mx-auto max-w-7xl'>
                        <SectionHeader
                            title='Resource Library'
                            subtitle='Explore our collection of curated resources'
                        />

                        <div className='px-4 pt-4 sm:max-w-6xl sm:mx-auto'>
                            <span
                                onClick={() => navigate('/resource-landing')}
                                className='inline-flex items-center space-x-2 transition-colors cursor-pointer text-cyan-600 hover:text-cyan-700'
                            >
                                <ArrowLeft className='w-5 h-5 shrink-0' />
                                <span className='font-medium'>Back to Resources</span>
                            </span>
                        </div>

                        <div className='px-4 sm:max-w-6xl sm:mx-auto'>
                            <div className='flex items-center gap-3 px-1 py-1'>
                                <FilterInput {...filter} />
                            </div>
                        </div>

                        <main className='px-6 py-2 sm:mx-10'>
                            <div className={'grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3'}>
                                {paginatedData?.map((resource, index) => (
                                    <ResourceCard
                                        key={resource._id}
                                        resource={resource}
                                        isFavorite={user?.favoriteResources.some(
                                            (favRes) => favRes._id === resource._id
                                        )}
                                        onToggleFavorite={handleToggleFavorite}
                                        isFirst={index === 0}
                                    />
                                ))}
                            </div>

                            <Loading mode='block' isLoading={isLoading} text='Loading resources...' />

                            {!isLoading && filteredData && filteredData.length === 0 && (
                                <div className='flex flex-col items-center justify-center px-6 py-16 text-center bg-white border-2 border-gray-200 border-dashed rounded-lg'>
                                    <Folder className='w-12 h-12 mb-4 text-gray-300' />
                                    <p className='font-medium text-gray-500'>No resources found</p>
                                    <p className='mt-1 text-sm text-gray-400'>
                                        {selectedCategory && selectedCategory !== 'All Resources'
                                            ? `We're still adding resources to ${selectedCategory}. Check back soon, or browse all resources below.`
                                            : 'Resources will appear here'}
                                    </p>
                                    {selectedCategory && selectedCategory !== 'All Resources' && (
                                        <span
                                            onClick={() => navigate('/resource-library?category=All Resources')}
                                            className='px-4 py-2 mt-4 text-white transition-colors rounded-lg cursor-pointer bg-brand-600 hover:bg-brand-700'
                                        >
                                            View all resources
                                        </span>
                                    )}
                                </div>
                            )}

                            {totalPages > 1 && (
                                <CustomPagination
                                    currentPage={currentPage}
                                    totalPages={totalPages}
                                    onPageChange={setCurrentPage}
                                />
                            )}
                        </main>
                    </div>
                )
            }}
        </DataView>
    )
}

interface CustomPaginationProps {
    currentPage: number
    totalPages: number
    onPageChange: (page: number) => void
}

function CustomPagination({ currentPage, totalPages, onPageChange }: CustomPaginationProps) {
    return (
        <div className='flex items-center justify-center gap-2 mt-8'>
            <button
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className='flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
            >
                <ChevronLeft className='w-4 h-4' />
                Previous
            </button>

            <span className='px-4 py-2 text-sm text-gray-600'>
                Page {currentPage} of {totalPages}
            </span>

            <button
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className='flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
            >
                Next
                <ChevronRight className='w-4 h-4' />
            </button>
        </div>
    )
}

interface ResourceCardProps {
    resource: IPublicResource
    isFavorite: boolean
    onToggleFavorite: (resourceId: string) => void
    isFirst: boolean
}

function ResourceCard(props: ResourceCardProps) {
    const { resource, isFavorite, onToggleFavorite, isFirst } = props
    const navigate = useNavigate()
    return (
        <section
            onClick={() => {
                analytics.trackResourceView(resource._id, resource.category || resource.category || 'unknown')
                navigate(`/resource/${resource?._id}`)
            }}
            className={`bg-white rounded-lg shadow-sm hover:shadow-md transition-all cursor-pointer ${
                isFirst ? 'border-l-4 border-l-brand-500' : ''
            }`}
        >
            <div className='p-4'>
                <div className='flex flex-col items-start gap-3 mx-1 mb-3 border-b border-gray-100 just'>
                    <div className='flex justify-between w-full space-x-2'>
                        <div
                            className={`
                        w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0
                        ${isFirst ? 'bg-brand-200/50 text-brand-700 font-extrabold' : 'bg-gray-200 text-gray-600'}
                    `}
                        >
                            <BookOpen className='w-4 h-4' />
                        </div>
                        <div className='flex-1 '>
                            <div className='flex items-start justify-between gap-2'>
                                <h3 className='ml-3 font-semibold text-gray-900 sm:text-lg line-clamp-2'>
                                    {resource.title}
                                </h3>
                                <span
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        analytics.trackResourceFavorite(resource._id, !isFavorite)
                                        onToggleFavorite(resource._id)
                                    }}
                                    className='p-1.5 rounded-full hover:bg-gray-100 transition-colors flex-shrink-0'
                                    aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                                >
                                    <Bookmark
                                        className={`w-4 h-4 ${isFavorite ? 'text-red-500' : 'text-gray-400'}`}
                                        fill={isFavorite ? 'currentColor' : 'none'}
                                    />
                                </span>
                            </div>
                        </div>
                    </div>

                    {resource.category && (
                        <span className='inline-flex items-center gap-1 px-2 py-1 mb-2 ml-10 text-xs font-medium rounded-full bg-brand-50 text-brand-700'>
                            <Folder className='w-3 h-3' />
                            {resource.category}
                        </span>
                    )}
                </div>

                {resource.description && (
                    <p className='mb-3 text-sm text-gray-600 line-clamp-2 ml-11'>{resource.description}</p>
                )}

                <div className='flex items-center justify-between pt-3 mt-4 border-t border-gray-100 ml-11'>
                    <span
                        onClick={() => navigate(`/resource/${resource?._id}`)}
                        className='inline-flex items-center gap-1 px-3 py-1.5 bg-brand-600 text-white rounded-full text-xs font-medium hover:bg-brand-700 transition-colors'
                    >
                        {resource.resourceType === 'file' || (resource.s3ObjectId && !resource.link) ? (
                            <>
                                View File
                                <FileText className='w-3.5 h-3.5' />
                            </>
                        ) : (
                            <>
                                Learn More
                                <ExternalLink className='w-3.5 h-3.5' />
                            </>
                        )}
                    </span>
                </div>
            </div>
        </section>
    )
}
